import { useFieldArray, useForm } from 'react-hook-form';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { Box, Button, IconButton, Stack, styled } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { InventoryManagementSettingFormRef } from './InventoryManagementDialog';
import TimeInput from '../../../forms/TimeInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

const PREFIX = 'PackageOfferTimeForm';

const classes = {
  addButton: `${PREFIX}-addButton`,
  timeContainer: `${PREFIX}-timeContainer`,
  timeInput: `${PREFIX}-timeInput`,
  deleteButtonContainer: `${PREFIX}-deleteButtonContainer`,
};

const StyledStack = styled(Stack)(({ theme }) => ({
  [`& .${classes.addButton}`]: {
    ...theme.typography.body1,
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: 76,
    height: 24,
  },
  [`& .${classes.timeContainer}`]: {
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.timeInput}`]: {
    width: 140,
  },
  [`& .${classes.deleteButtonContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
}));

interface PackageOfferTimeFormProps {
  value?: string[];
  onValidityChange?: (valid: boolean) => void;
  isReadOnly?: boolean;
}

interface PackageOfferTimeFormValues {
  packageOfferingTimes: { value: DateTime }[];
}

export const PackageOfferTimeForm = forwardRef<
  InventoryManagementSettingFormRef,
  PackageOfferTimeFormProps
>(({ value, onValidityChange, isReadOnly }, ref) => {
  const t = useCommonTranslation();
  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
  } = useForm<PackageOfferTimeFormValues>({
    mode: 'onChange',
    defaultValues: {
      packageOfferingTimes: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'packageOfferingTimes',
  });
  const requiredFieldMessage = t('common.requiredField');

  useImperativeHandle(ref, () => ({
    getValues,
    triggerValidation: () => trigger(),
    isValid,
  }));

  useEffect(() => {
    if (value != null && value.length > 0) {
      const dateTimes = value.map((time) => ({
        value: DateTime.fromFormat(time, 'HH:mm:ss'),
      }));
      setValue('packageOfferingTimes', dateTimes);
    } else {
      setValue('packageOfferingTimes', [
        { value: DateTime.now().startOf('day') },
      ]);
    }
  }, [value]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleAddOffer = () => {
    append({ value: DateTime.now().startOf('day') });
  };

  const handleRemoveOffer = (index: number) => {
    remove(index);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <StyledStack direction="column">
        {fields.map((field, index) => (
          <Stack
            key={`packageOfferingTimes${index}`}
            direction="row"
            className={classes.timeContainer}>
            <Box className={classes.timeInput}>
              <TimeInput
                label={`${t('inventoryManagement.offer')} ${index + 1}`}
                name={`packageOfferingTimes.${index}.value`}
                required
                control={control}
                readOnly={isReadOnly}
              />
            </Box>
            {index !== 0 && !isReadOnly && (
              <Box className={classes.deleteButtonContainer}>
                <IconButton
                  aria-label="delete"
                  onClick={() => handleRemoveOffer(index)}>
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </Stack>
        ))}

        {!isReadOnly && (
          <Button className={classes.addButton} onClick={handleAddOffer}>
            + {t('inventoryManagement.addOffer')}
          </Button>
        )}
      </StyledStack>
    </LocalizationProvider>
  );
});
